// Fleet desk modules. Each face is a summary tile fed by the SAME TanStack
// query keys/options as the full panel behind its drawer, so opening the
// drawer is a cache hit. Faces are state-only: no buttons or links inside.
//
// Loading rule (plan Review Focus 2, see useBooks.ts): a query that is
// `isLoading` OR (`isPaused && data === undefined`) is still LOADING — never
// a resolved zero. While loading, faces render '—' and omit compare/footer so
// no `$0` / `0/25` / `NaN` can leak.
import type { ReactElement } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  accountApi,
  auditApi,
  benchmarkApi,
  convictionApi,
  engineApi,
  positionsApi,
  riskApi,
} from '../../api/client'
import { useTimeframe } from '../../hooks/useTimeframe'
import { bookWindowReturn, buildPickBars } from '../../lib/pickPerformance'
import { relativeTime } from '../../lib/relativeTime'
import { spyReturnForTimeframe } from '../../lib/spyVerdict'
import { timeframeLabel, toEquityRange } from '../../lib/timeframe'
import { PipStrip } from '../viz/PipStrip'
import { ProgressBar } from '../viz/ProgressBar'
import { Sparkline } from '../viz/Sparkline'
import { StatModule } from './StatModule'
import {
  bookCountMarker,
  DASH,
  liveBooks,
  num,
  POSITION_CAP,
  ROLE_INITIAL,
  RUNS_LIMIT,
  signTone,
  stillLoading,
  unavailable,
  useBookAccounts,
  usePicksCaveats,
  type FleetModuleProps,
} from './fleetModuleHooks'
import {
  drawdownFace,
  exposure,
  fleetEquity,
  fmtPct,
  fmtSignedUsd,
  fmtUsd,
  runPips,
  sumEquitySeries,
  worstDrawdown,
} from './fleetSummaries'
import { withMarker } from './statCompare'

// ------------------------------------------------------------ fleet equity

export function FleetEquityModule({ books, booksPending, booksError }: FleetModuleProps) {
  const accounts = useBookAccounts(books)
  const histories = useQueries({
    queries: accounts.live.map((b) => ({
      queryKey: ['equity-history', b.id, '1M'],
      queryFn: () => accountApi.history(b.id, '1M'),
      retry: false,
      staleTime: 300000,
    })),
  })

  const isLoading = booksPending || accounts.loading
  const none = accounts.resolved.length === 0
  // No non-dormant book resolved and something failed → error rung.
  const isError = !isLoading && none && (booksError || accounts.unavailable > 0)
  const partial = accounts.unavailable > 0
  const { equity, pnl, pct } = fleetEquity(accounts.resolved.map((r) => r.account))
  // Withhold the line while any history is unsettled or any book is unknown:
  // a partial sum is a wrong fleet line.
  const showSeries = !partial && !histories.some((h) => stillLoading(h) || unavailable(h))
  const series = showSeries ? sumEquitySeries(histories.map((h) => h.data)) : []

  return (
    <StatModule
      title="Fleet equity"
      drawerKey="fleet-equity"
      value={none ? DASH : fmtUsd(equity)}
      tone={partial ? 'warn' : 'neutral'}
      isLoading={isLoading}
      isError={isError}
      errorText="accounts unavailable"
      compare={
        isLoading
          ? undefined
          : none
            ? 'no live books'
            : withMarker(
                `${fmtSignedUsd(pnl)} · ${pct === null ? DASH : fmtPct(pct)} today`,
                bookCountMarker(accounts.unavailable, 'unavailable'),
              )
      }
      footer={
        showSeries ? <Sparkline values={series} color="var(--series-1)" ariaLabel="fleet equity 1M" /> : undefined
      }
    />
  )
}

// ---------------------------------------------------------------- fleet P&L

export function FleetPnlModule({ books, booksPending, booksError }: FleetModuleProps) {
  const accounts = useBookAccounts(books)
  const research = books.find((b) => b.role === 'research')
  const historyQ = useQuery({
    queryKey: ['equity-history', research?.id, '1W'],
    queryFn: () => accountApi.history(research?.id, '1W'),
    enabled: research !== undefined,
    retry: false,
    staleTime: 300000,
  })

  const isLoading = booksPending || accounts.loading
  const none = accounts.resolved.length === 0
  const isError = !isLoading && none && (booksError || accounts.unavailable > 0)
  const { pnl } = fleetEquity(accounts.resolved.map((r) => r.account))
  const perBook = accounts.resolved
    .map(({ book, account }) => {
      const v = num(account.daily_pnl)
      return `${ROLE_INITIAL[book.role]} ${v === null ? DASH : fmtSignedUsd(v)}`
    })
    .join(' · ')
  const series = (historyQ.data?.points ?? [])
    .map((p) => num(p.equity))
    .filter((v): v is number => v !== null)

  return (
    <StatModule
      title="Day P&L"
      drawerKey="fleet-pnl"
      value={none ? DASH : fmtSignedUsd(pnl)}
      tone={none ? 'neutral' : signTone(pnl)}
      isLoading={isLoading}
      isError={isError}
      errorText="accounts unavailable"
      compare={
        isLoading
          ? undefined
          : none
            ? 'no live books'
            : withMarker(
                // The footer sparkline is the RESEARCH book's 1W equity, not
                // the fleet's: label it whenever it renders (≥ 2 points).
                series.length >= 2 ? `${perBook} · R 1W` : perBook,
                bookCountMarker(accounts.unavailable, 'unavailable'),
              )
      }
      footer={<Sparkline values={series} color="var(--series-1)" ariaLabel="research 1W" />}
    />
  )
}

// ------------------------------------------------------------ picks excess

/**
 * Same inputs and options as PicksPanel (lines 36-131) so the drawer is a
 * cache hit; the value is the BOOK's window return minus SPY's
 * (`bookReturn.pct - spyPct`), not a per-pick excess.
 */
export function PicksExcessModule({ books, booksPending, booksError }: FleetModuleProps) {
  const { timeframe } = useTimeframe()
  const research = books.find((b) => b.role === 'research')
  const accountId = research?.id

  const positionsQ = useQuery({
    queryKey: ['positions', accountId],
    queryFn: () => positionsApi.getAll(accountId),
    enabled: accountId !== undefined,
    refetchInterval: 10000,
  })
  const scorecardQ = useQuery({
    queryKey: ['scorecard', accountId],
    queryFn: () => convictionApi.scorecard(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    refetchInterval: 30000,
  })
  const accountQ = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountApi.get(accountId),
    enabled: accountId !== undefined,
    refetchInterval: 10000,
  })
  const historyQ = useQuery({
    queryKey: ['equity-history', accountId, toEquityRange(timeframe)],
    queryFn: () => accountApi.history(accountId, toEquityRange(timeframe)),
    enabled: accountId !== undefined && timeframe !== 'DAY',
    retry: 1,
    staleTime: 300000,
  })
  const benchQ = useQuery({
    queryKey: ['benchmark-returns'],
    queryFn: () => benchmarkApi.returns(),
    retry: 1,
    staleTime: 60000,
    refetchInterval: 300000,
  })

  // As PicksPanel: a disabled query is never "loading", so an unresolved
  // roster must count as loading explicitly.
  const booksUnresolved = booksPending || (research === undefined && !booksError)
  const isLoading =
    booksUnresolved ||
    [positionsQ, scorecardQ, accountQ, benchQ].some(stillLoading) ||
    (timeframe !== 'DAY' && stillLoading(historyQ))
  const isError = !isLoading && (research === undefined || unavailable(positionsQ) || unavailable(accountQ))

  const { bars } = buildPickBars(timeframe, positionsQ.data ?? [], scorecardQ.data?.positions ?? [])
  const winners = bars.filter((b) => b.returnPct > 0).length
  const losers = bars.filter((b) => b.returnPct < 0).length

  const spyPct = spyReturnForTimeframe(timeframe, benchQ.data)
  const caveats = usePicksCaveats(benchQ.data)
  const account = accountQ.data
  const rawNetFlow = historyQ.data?.net_flow != null ? Number(historyQ.data.net_flow) : null
  const netFlow = rawNetFlow !== null && Number.isFinite(rawNetFlow) ? rawNetFlow : null
  const bookReturn = account
    ? bookWindowReturn(
        timeframe,
        { daily_pnl: Number(account.daily_pnl), account_value: Number(account.account_value) },
        historyQ.data?.points ?? [],
        netFlow,
      )
    : { pct: null, flowAdjusted: true }
  const excess = bookReturn.pct !== null && spyPct !== null ? bookReturn.pct - spyPct : null
  const isDay = timeframe === 'DAY'
  const compareText = [
    `${winners} up · ${losers} down`,
    isDay && caveats.spyDayEnd ? `SPY ${caveats.spyDayEnd}` : null,
    isDay && caveats.marketClosed ? 'last session' : null,
    bookReturn.pct !== null && !bookReturn.flowAdjusted ? 'raw (no flows)' : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <StatModule
      title="Picks vs SPY"
      caption={`research · ${timeframeLabel(timeframe)}`}
      drawerKey="picks-excess"
      value={excess === null ? DASH : fmtPct(excess)}
      tone={excess === null ? 'neutral' : excess > 0 ? 'gain' : excess < 0 ? 'loss' : 'neutral'}
      isLoading={isLoading}
      isError={isError}
      errorText={research === undefined ? 'books unavailable' : 'picks unavailable'}
      compare={isLoading ? undefined : compareText}
    />
  )
}

// ---------------------------------------------------------- drawdown floor

export function DrawdownFloorModule({ books, booksPending, booksError }: FleetModuleProps) {
  const live = liveBooks(books)
  const results = useQueries({
    queries: live.map((b) => ({
      queryKey: ['drawdown-floor', b.id],
      queryFn: () => riskApi.drawdownFloor(b.id),
      retry: 1, // as RiskBackstopCard (the drawer panel)
      refetchInterval: 60000,
    })),
  })

  const isLoading = booksPending || results.some(stillLoading)
  const unknown = results.filter(unavailable).length
  const noneResolved = results.every((r) => r.data === undefined)
  const isError = !isLoading && noneResolved && (unknown > 0 || booksError)
  const face = drawdownFace(
    worstDrawdown(live.map((b, i) => ({ status: results[i]?.data, label: b.role }))),
    unknown,
  )

  return (
    <StatModule
      title="Drawdown floor"
      drawerKey="drawdown-floor"
      value={face.value}
      tone={face.tone}
      isLoading={isLoading}
      isError={isError}
      errorText="drawdown unavailable"
      compare={isLoading ? undefined : withMarker(face.base, face.marker)}
      footer={face.bar ? <ProgressBar {...face.bar} /> : undefined}
    />
  )
}

// ---------------------------------------------------------------- exposure

export function ExposureModule({ books, booksPending, booksError }: FleetModuleProps) {
  const research = books.find((b) => b.role === 'research')
  const accountId = research?.id

  const positionsQ = useQuery({
    queryKey: ['positions', accountId],
    queryFn: () => positionsApi.getAll(accountId),
    enabled: accountId !== undefined,
    refetchInterval: 10000,
  })
  const accountQ = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountApi.get(accountId),
    enabled: accountId !== undefined,
    retry: false,
    refetchInterval: 10000,
  })
  const convictionQ = useQuery({
    queryKey: ['conviction-status', accountId],
    queryFn: () => convictionApi.status(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    refetchInterval: 30000,
  })

  const booksUnresolved = booksPending || (research === undefined && !booksError)
  const isLoading = booksUnresolved || stillLoading(positionsQ) || stillLoading(accountQ)
  const isError = !isLoading && (research === undefined || unavailable(positionsQ) || unavailable(accountQ))
  const { ratio, cash } = exposure(positionsQ.data, accountQ.data)
  // Held count is secondary: '—' until the conviction status resolves, never 0.
  const heldRaw = convictionQ.data ? num(convictionQ.data.candidates_by_state?.held) ?? 0 : null
  const held = heldRaw === null ? DASH : String(heldRaw)

  return (
    <StatModule
      title="Exposure"
      caption="research"
      drawerKey="exposure"
      value={ratio === null ? DASH : `${ratio.toFixed(2)}×`}
      isLoading={isLoading}
      isError={isError}
      errorText={research === undefined ? 'books unavailable' : 'exposure unavailable'}
      compare={
        isLoading ? undefined : `cash ${cash === null ? DASH : fmtUsd(cash)} · ${held}/${POSITION_CAP} held`
      }
      footer={
        ratio === null ? undefined : <ProgressBar value={ratio} max={1} ariaLabel={`gross exposure ${ratio.toFixed(2)}×`} />
      }
    />
  )
}

// ---------------------------------------------------------- engine + alarms

// Takes the shared FleetModuleProps for a uniform page call site; its state is
// fleet-wide (engine + runs), so it reads none of them.
export const EngineAlarmsModule: (props: FleetModuleProps) => ReactElement = function EngineAlarmsModule() {
  const engineQ = useQuery({
    queryKey: ['engine-status'],
    queryFn: engineApi.getStatus,
    refetchInterval: 60000,
    retry: 1,
  })
  const runsQ = useQuery({
    queryKey: ['workflow-runs', 1],
    queryFn: () => auditApi.listRuns(RUNS_LIMIT, 1),
    refetchInterval: 10000,
  })
  // No ['cloudwatch-metrics'] read here (review I6): the endpoint is an
  // uncached 450-day GetMetricData call and the face shows none of it, so
  // only the drawer's CloudWatch panel fetches it, on open. GAP (spec §6.4):
  // the payload carries metric series only, no alarm states, so the face
  // shows no alarm count until the endpoint exposes one.

  const isLoading = stillLoading(engineQ)
  const isError = !isLoading && engineQ.isError && engineQ.data === undefined
  const status = engineQ.data?.status
  const runs = runsQ.data?.runs
  const fails = runs ? String(runs.filter((r) => !r.success).length) : DASH

  return (
    <StatModule
      title="Engine"
      drawerKey="engine-alarms"
      value={status ? status.toUpperCase() : DASH}
      tone={status === 'running' ? 'gain' : 'warn'}
      isLoading={isLoading}
      isError={isError}
      errorText="engine status unavailable"
      compare={
        isLoading
          ? undefined
          : `tick ${relativeTime(engineQ.data?.last_workflow_run)} · ${fails} failed / ${RUNS_LIMIT}`
      }
      footer={runs ? <PipStrip pips={runPips(runs, RUNS_LIMIT)} ariaLabel="recent workflow runs" /> : undefined}
    />
  )
}
